<script setup>
/**
 * Dr. Clinix (DrWeb) — the conversation-sidebar panel. Plan: docs/smarticks/drweb-integration-plan.md
 * (§2 D4, §3 WP-4, §7a W5/W6) and the UI/UX reviews under docs/smarticks/reviews/.
 *
 * One accordion, four flat groups: the match line, upcoming appointments, open tickets, actions.
 * Mounted by ContactPanel.vue inside an `AccordionItem compact` whose body is `v-if="isOpen"`, so
 * "load when the accordion opens" IS mount — nothing loads for a closed accordion — and a
 * conversation change re-fetches through the watcher below.
 *
 * 🔴 Four distinct states, four distinct strings. A failed load must never render as an empty
 * list, and "no patient for this number" must never share a string with "patient found, no
 * appointments" — the reminders-screen 401 lesson (RemindersIndex.vue) and the UX review a.5.
 *
 * 🔴 Every date, time, patient number and ticket number sits in a `<bdi>`. `12/03 14:30` next
 * to Hebrew reorders on screen otherwise, and a receptionist acts on the wrong row.
 *
 * Writes (remark / ticket / pick a patient) are admin-only server-side (Pundit + admin check);
 * remark/ticket stay admin-only and are hidden for agents rather than shown to 401; since v1.1
 * (owner decision 2026-09-10) book and cancel are offered to every agent who can open the conversation.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import SmarticksAPI from 'dashboard/api/smarticks';
import { useDrwebStatus } from './useDrwebStatus';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  // The conversation's display_id — what the route carries and what the endpoint takes.
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const { drwebTenantUrl } = useDrwebStatus();

const currentChat = useMapGetter('getSelectedChat');
const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(() => currentRole.value === 'administrator');
const contactPhone = computed(
  () => currentChat.value?.meta?.sender?.phone_number || ''
);

const VISIBLE_ROWS = 3;
// Ticket statuses measured on the test tenant (plan §12): 1 new · 2 in progress · 3 closed ·
// 4 meeting set · 5 quote issued · 6 won · 7 no deal · 8 irrelevant. "Open" is what still
// needs a person; an unknown status is shown rather than hidden.
const OPEN_TICKET_STATUSES = [1, 2, 4, 5];
// Load errors the receptionist can do something about by retrying. `invalid_credential` and
// `disabled` need an admin; `forbidden` (Pundit 401) needs an inbox membership.
const RETRYABLE = ['unreachable'];

// ── load state ─────────────────────────────────────────────────────────────────────────────
const state = ref('loading'); // loading | error | ready
const errorKind = ref('unreachable'); // unreachable | auth | disabled | forbidden
const failedAttempts = ref(0);
const patient = ref({});
const appointments = ref([]);
const tickets = ref([]);
const ticketsFailed = ref(false);
// v1.1 WP-A: the roster the book dialog offers (hidden cells already dropped server-side)
const therapists = ref([]);
let requestSeq = 0;

const classifyFailure = (code, httpStatus) => {
  if (httpStatus === 401) return 'forbidden';
  if (code === 'invalid_credential') return 'auth';
  if (code === 'disabled') return 'disabled';
  return 'unreachable';
};

const load = async () => {
  requestSeq += 1;
  const seq = requestSeq;
  state.value = 'loading';
  try {
    const { data } = await SmarticksAPI.drwebConversation(props.conversationId);
    if (seq !== requestSeq) return; // the conversation changed under us
    if (!data?.ok) {
      errorKind.value = classifyFailure(data?.code);
      failedAttempts.value += 1;
      state.value = 'error';
      return;
    }
    patient.value = data.patient || {};
    appointments.value = Array.isArray(data.appointments)
      ? data.appointments
      : [];
    // The tickets section fails on its own server-side ({error} instead of a list) so a CRM
    // hiccup does not blank the patient card — mirror that: a section-level line, not a panel error.
    ticketsFailed.value = !Array.isArray(data.tickets);
    tickets.value = ticketsFailed.value ? [] : data.tickets;
    therapists.value = Array.isArray(data.therapists) ? data.therapists : [];
    failedAttempts.value = 0;
    state.value = 'ready';
  } catch (error) {
    if (seq !== requestSeq) return;
    therapists.value = [];
    errorKind.value = classifyFailure(
      error?.response?.data?.code,
      error?.response?.status
    );
    failedAttempts.value += 1;
    state.value = 'error';
  }
};

onMounted(load);
watch(
  () => props.conversationId,
  (next, prev) => {
    if (next && next !== prev) load();
  }
);

const isRetryable = computed(() => RETRYABLE.includes(errorKind.value));
const errorText = computed(() =>
  t(`SMARTICKS.DRWEB.ERROR.${errorKind.value.toUpperCase()}`)
);

// ── derived ────────────────────────────────────────────────────────────────────────────────
const isMatched = computed(() => !!patient.value?.customer_id);
const patientError = computed(() => patient.value?.error || null);
const candidates = computed(() =>
  Array.isArray(patient.value?.candidates) ? patient.value.candidates : []
);
const hasPhone = computed(() => patientError.value !== 'no_phone');

const visibleAppointments = computed(() =>
  appointments.value.slice(0, VISIBLE_ROWS)
);
const hiddenAppointmentsCount = computed(() =>
  Math.max(appointments.value.length - VISIBLE_ROWS, 0)
);

const openTickets = computed(() =>
  tickets.value.filter(ticket => {
    const status = Number(ticket.status);
    return Number.isNaN(status) || OPEN_TICKET_STATUSES.includes(status);
  })
);
const visibleTickets = computed(() => openTickets.value.slice(0, VISIBLE_ROWS));
const hiddenTicketsCount = computed(() =>
  Math.max(openTickets.value.length - VISIBLE_ROWS, 0)
);

// `at` is the clinic's wall clock, `YYYY-MM-DDTHH:MM` (Booking#item). Split, never `new Date()`:
// a browser in another zone would shift the hour, and the weekday comes from the date part alone.
const formatAt = at => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(at || '');
  if (!match) return { date: at || '', time: '', weekday: '' };
  const [, y, m, d, hh, mm] = match;
  const weekday = new Date(Date.UTC(+y, +m - 1, +d)).getUTCDay();
  return {
    date: `${d}/${m}/${y}`,
    time: `${hh}:${mm}`,
    weekday: t(`SMARTICKS.DRWEB.WEEKDAY.${weekday}`),
  };
};

const formatDate = raw => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw || '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : raw || '';
};

const POLICY_CLASS = {
  book: 'bg-n-teal-3 text-n-teal-11',
  info: 'bg-n-slate-3 text-n-slate-11',
  hidden: 'bg-n-amber-9/10 text-n-amber-11',
};
const policyClass = mode => POLICY_CLASS[mode] || POLICY_CLASS.info;
const policyLabel = mode =>
  t(
    `SMARTICKS.DRWEB.POLICY.${(POLICY_CLASS[mode] ? mode : 'info').toUpperCase()}`
  );

const ticketStatusLabel = status => {
  const code = Number(status);
  return code >= 1 && code <= 8
    ? t(`SMARTICKS.DRWEB.TICKETS.STATUS.${code}`)
    : '';
};

// ── writes ─────────────────────────────────────────────────────────────────────────────────
const moreDialog = ref(null);
const remarkDialog = ref(null);
const ticketDialog = ref(null);

const remarkIndex = ref(null);
const remarkText = ref('');
const ticketText = ref('');
const saving = ref(false);
const choosing = ref(null);

const openRemarkDialog = () => {
  remarkIndex.value = appointments.value[0]?.index ?? null;
  remarkText.value = '';
  remarkDialog.value?.open();
};

const openTicketDialog = () => {
  ticketText.value = '';
  ticketDialog.value?.open();
};

// Every write reports through useAlert (UX a.10): the receptionist has no other window on DrWeb.
// The server's `message` is Hebrew by construction (W6, Smarticks::Drweb::Messages); the i18n
// string is the fallback for a thrown request.
const reportWrite = (data, doneKey) => {
  if (data?.ok) {
    useAlert(data.message || t(doneKey));
    return true;
  }
  useAlert(data?.message || t('SMARTICKS.DRWEB.WRITE_FAILED'));
  return false;
};

const submitRemark = async () => {
  if (!remarkText.value.trim() || remarkIndex.value === null) return;
  saving.value = true;
  try {
    const { data } = await SmarticksAPI.drwebRemark(props.conversationId, {
      index: remarkIndex.value,
      appointment_id: appointments.value.find(
        a => a.index === remarkIndex.value
      )?.id,
      text: remarkText.value.trim(),
    });
    if (reportWrite(data, 'SMARTICKS.DRWEB.REMARK_DIALOG.DONE')) {
      remarkDialog.value?.close();
      load();
    }
  } catch (error) {
    useAlert(t('SMARTICKS.DRWEB.WRITE_FAILED'));
  } finally {
    saving.value = false;
  }
};

const submitTicket = async () => {
  if (!ticketText.value.trim()) return;
  saving.value = true;
  try {
    const { data } = await SmarticksAPI.drwebTicket(props.conversationId, {
      details: ticketText.value.trim(),
    });
    if (reportWrite(data, 'SMARTICKS.DRWEB.TICKET_DIALOG.DONE')) {
      ticketDialog.value?.close();
      load();
    }
  } catch (error) {
    useAlert(t('SMARTICKS.DRWEB.WRITE_FAILED'));
  } finally {
    saving.value = false;
  }
};

// ── v1.1 WP-A: book + cancel from the panel ───────────────────────────────────────────────
// Every Button inside a Dialog slot carries type="button": Dialog wraps its slot in a <form> and a
// bare <button> submits it. Every date/time goes through formatAt() into its own <bdi>.
const bookDialog = ref(null);
const cancelDialog = ref(null);
const bookTherapistId = ref(null);
const bookBranchId = ref(null);
const bookDate = ref('');
const slots = ref([]);
const slotsState = ref('idle'); // idle | loading | empty | error | ready
const slotsError = ref('');
const selectedSlot = ref(null);
const cancelItem = ref(null);
const cancelReason = ref('');

const localIsoDate = (daysAhead = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return d.toLocaleDateString('en-CA'); // YYYY-MM-DD in the browser's zone
};
const bookMinDate = localIsoDate(0);
const bookMaxDate = localIsoDate(90);

const bookTherapist = computed(
  () => therapists.value.find(th => th.id === bookTherapistId.value) || null
);
const bookBranches = computed(() => bookTherapist.value?.branches || []);
const bookBranch = computed(
  () => bookBranches.value.find(b => b.id === bookBranchId.value) || null
);
// The native date input renders in the BROWSER's locale (MM/DD/YYYY on an en-US browser — Viki,
// 2026-09-10); the echo beside it is the clinic's format, weekday first.
const bookDateWeekday = computed(() =>
  bookDate.value
    ? `${formatAt(`${bookDate.value}T00:00`).weekday} ${formatDate(bookDate.value)}`
    : ''
);
const canSearchSlots = computed(
  () => !!(bookTherapistId.value && bookBranchId.value && bookDate.value)
);
const isSelected = slot => selectedSlot.value?.from === slot.from;

const resetSlots = () => {
  slots.value = [];
  selectedSlot.value = null;
  slotsState.value = 'idle';
  slotsError.value = '';
};
watch(bookTherapistId, () => {
  bookBranchId.value =
    bookBranches.value.length === 1 ? bookBranches.value[0].id : null;
  resetSlots();
});
watch([bookBranchId, bookDate], resetSlots);

const openBookDialog = () => {
  bookTherapistId.value =
    therapists.value.length === 1 ? therapists.value[0].id : null;
  bookBranchId.value =
    bookBranches.value.length === 1 ? bookBranches.value[0].id : null;
  bookDate.value = bookMinDate;
  resetSlots();
  bookDialog.value?.open();
};

// A live vendor call under the 5-call / 8-second budget: "not searched yet" and "searched,
// nothing free" are different facts and never share a string.
const loadSlots = async () => {
  if (!canSearchSlots.value) return;
  slotsState.value = 'loading';
  selectedSlot.value = null;
  try {
    const { data } = await SmarticksAPI.drwebFreeTime(props.conversationId, {
      scheduler_id: bookTherapistId.value,
      branch_id: bookBranchId.value,
      date: bookDate.value,
    });
    if (!data?.ok) {
      slotsError.value = data?.message || t('SMARTICKS.DRWEB.WRITE_FAILED');
      slotsState.value = 'error';
      return;
    }
    slots.value = Array.isArray(data.slots) ? data.slots : [];
    slotsState.value = slots.value.length ? 'ready' : 'empty';
  } catch (error) {
    slotsError.value = t('SMARTICKS.DRWEB.WRITE_FAILED');
    slotsState.value = 'error';
  }
};

const submitBook = async () => {
  if (!selectedSlot.value) return;
  saving.value = true;
  try {
    const { data } = await SmarticksAPI.drwebBook(props.conversationId, {
      scheduler_id: bookTherapistId.value,
      branch_id: bookBranchId.value,
      at: selectedSlot.value.from,
    });
    if (reportWrite(data, 'SMARTICKS.DRWEB.BOOK_DIALOG.DONE')) {
      bookDialog.value?.close();
      load();
    }
  } catch (error) {
    useAlert(t('SMARTICKS.DRWEB.WRITE_FAILED'));
  } finally {
    saving.value = false;
  }
};

const canCancel = item =>
  item.status === 'booked' && (item.mode !== 'hidden' || item.bot_booked);

const openCancelDialog = item => {
  cancelItem.value = item;
  cancelReason.value = '';
  cancelDialog.value?.open();
};

// The dialog is the human's challenge; the server re-resolves the list and matches the id we
// saw (`list_changed` otherwise), so a row that shifted under us is never the one cancelled.
const submitCancel = async () => {
  if (!cancelItem.value) return;
  saving.value = true;
  try {
    const { data } = await SmarticksAPI.drwebCancel(props.conversationId, {
      index: cancelItem.value.index,
      appointment_id: cancelItem.value.id,
      reason: cancelReason.value.trim(),
    });
    if (reportWrite(data, 'SMARTICKS.DRWEB.CANCEL_DIALOG.DONE')) {
      cancelDialog.value?.close();
      load();
    }
  } catch (error) {
    useAlert(t('SMARTICKS.DRWEB.WRITE_FAILED'));
  } finally {
    saving.value = false;
  }
};

// The receptionist's pick among the candidates of a shared number (W5). Verified server-side
// against a fresh search, then remembered on the conversation.
const choosePatient = async candidate => {
  choosing.value = candidate.id;
  try {
    const { data } = await SmarticksAPI.drwebPatient(props.conversationId, {
      customerId: candidate.id,
    });
    if (reportWrite(data, 'SMARTICKS.DRWEB.PATIENT.CHOSEN')) load();
  } catch (error) {
    useAlert(t('SMARTICKS.DRWEB.WRITE_FAILED'));
  } finally {
    choosing.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col gap-3 px-3 py-2 text-sm">
    <!-- loading: skeleton rows, not a spinner — the row shapes are known -->
    <div
      v-if="state === 'loading'"
      class="flex flex-col gap-2"
      data-test="drweb-loading"
    >
      <div
        v-for="n in 3"
        :key="n"
        class="h-12 rounded-lg motion-safe:animate-pulse bg-n-alpha-1"
      />
    </div>

    <!-- error: icon + text, never colour alone; retry only where a retry can help -->
    <div
      v-else-if="state === 'error'"
      class="flex flex-col gap-2"
      data-test="drweb-error"
    >
      <p class="flex items-start gap-2 mb-0 text-sm text-n-slate-11" dir="auto">
        <Icon icon="i-lucide-plug-zap" class="shrink-0 mt-0.5 size-4" />
        <span>{{ errorText }}</span>
      </p>
      <Button
        v-if="isRetryable"
        variant="link"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('SMARTICKS.DRWEB.RETRY')"
        class="self-start"
        @click="load"
      />
      <p
        v-if="failedAttempts >= 2"
        class="mb-0 text-xs text-n-slate-11"
        dir="auto"
      >
        {{ t('SMARTICKS.DRWEB.ERROR.KEEP_GOING') }}
      </p>
    </div>

    <template v-else>
      <!-- 1 · the match line: only what DrWeb knows and ContactInfo above does not -->
      <div class="flex flex-col gap-1" data-test="drweb-patient">
        <p
          v-if="isMatched"
          class="flex items-center gap-1.5 mb-0 text-sm text-n-slate-12"
          dir="auto"
        >
          <Icon
            icon="i-lucide-user-round"
            class="shrink-0 size-4 text-n-slate-11"
          />
          <span>{{ t('SMARTICKS.DRWEB.PATIENT.LABEL') }}</span>
          <bdi class="font-inter tabular-nums">{{ patient.customer_id }}</bdi>
          <span v-if="patient.name" class="truncate" :title="patient.name">
            · {{ patient.name }}
          </span>
        </p>

        <template v-else-if="patientError === 'ambiguous'">
          <I18nT
            keypath="SMARTICKS.DRWEB.PATIENT.AMBIGUOUS"
            tag="p"
            class="mb-0 text-sm text-n-slate-12"
            dir="auto"
          >
            <template #phone>
              <bdi class="font-inter tabular-nums">{{ contactPhone }}</bdi>
            </template>
          </I18nT>
          <ul class="flex flex-col gap-1 p-0 m-0 list-none">
            <li
              v-for="candidate in candidates"
              :key="candidate.id"
              class="flex items-center justify-between gap-2 min-h-11 sm:min-h-0"
              dir="auto"
            >
              <span class="min-w-0 truncate" :title="candidate.name">
                {{ candidate.name }}
                <bdi class="text-xs font-inter tabular-nums text-n-slate-11">
                  #{{ candidate.id }}
                </bdi>
              </span>
              <Button
                v-if="isAdmin"
                size="xs"
                variant="faded"
                color="slate"
                :label="t('SMARTICKS.DRWEB.PATIENT.CHOOSE')"
                :is-loading="choosing === candidate.id"
                :disabled="choosing !== null"
                @click="choosePatient(candidate)"
              />
            </li>
          </ul>
          <p v-if="!isAdmin" class="mb-0 text-xs text-n-slate-11" dir="auto">
            {{ t('SMARTICKS.DRWEB.PATIENT.AMBIGUOUS_AGENT') }}
          </p>
        </template>

        <template v-else-if="patientError === 'not_found'">
          <I18nT
            keypath="SMARTICKS.DRWEB.PATIENT.NOT_FOUND"
            tag="p"
            class="mb-0 text-sm text-n-slate-12"
            dir="auto"
          >
            <template #phone>
              <bdi class="font-inter tabular-nums">{{ contactPhone }}</bdi>
            </template>
          </I18nT>
          <p class="mb-0 text-xs text-n-slate-11" dir="auto">
            {{ t('SMARTICKS.DRWEB.PATIENT.NOT_FOUND_HINT') }}
          </p>
        </template>

        <p v-else class="mb-0 text-sm text-n-slate-11" dir="auto">
          {{ t('SMARTICKS.DRWEB.PATIENT.NO_PHONE') }}
        </p>
      </div>

      <!-- 2 · upcoming appointments (only once there is a patient to list them for) -->
      <div
        v-if="isMatched"
        class="flex flex-col gap-2 pt-2 border-t border-n-weak"
        data-test="drweb-appointments"
      >
        <h6 class="mb-0 text-xs font-medium text-n-slate-11">
          {{ t('SMARTICKS.DRWEB.APPOINTMENTS.TITLE') }}
        </h6>
        <p
          v-if="!appointments.length"
          class="mb-0 text-sm text-n-slate-11"
          dir="auto"
        >
          {{ t('SMARTICKS.DRWEB.APPOINTMENTS.EMPTY') }}
        </p>
        <ul v-else class="flex flex-col gap-2 p-0 m-0 list-none">
          <li
            v-for="item in visibleAppointments"
            :key="item.id"
            class="flex flex-col gap-0.5 min-h-11 sm:min-h-0"
            dir="auto"
          >
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span class="text-xs text-n-slate-11">
                {{ formatAt(item.at).weekday }}
              </span>
              <bdi class="font-inter tabular-nums text-n-slate-12">
                {{ formatAt(item.at).date }}
              </bdi>
              <bdi class="font-inter tabular-nums text-n-slate-12">
                {{ formatAt(item.at).time }}
              </bdi>
              <span
                class="text-xs px-2 py-0.5 rounded-full"
                :class="
                  item.status === 'cancelled'
                    ? 'bg-n-slate-3 text-n-ruby-11'
                    : 'bg-n-teal-3 text-n-teal-11'
                "
              >
                {{
                  t(
                    `SMARTICKS.DRWEB.APPOINTMENTS.STATUS.${
                      item.status === 'cancelled' ? 'CANCELLED' : 'BOOKED'
                    }`
                  )
                }}
              </span>
              <Button
                v-if="canCancel(item)"
                type="button"
                size="sm"
                variant="ghost"
                color="ruby"
                icon="i-lucide-calendar-x"
                :label="t('SMARTICKS.DRWEB.ACTIONS.CANCEL_ITEM')"
                class="ms-auto"
                data-test="drweb-cancel-item"
                @click="openCancelDialog(item)"
              />
            </div>
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="text-xs text-n-slate-11 truncate"
                :title="`${item.therapist || ''} · ${item.branch || ''}`"
              >
                {{ item.therapist }} · {{ item.branch }}
              </span>
              <span
                class="shrink-0 text-xs px-2 py-0.5 rounded-full"
                :class="policyClass(item.mode)"
                :title="t('SMARTICKS.DRWEB.POLICY.HINT')"
              >
                {{ policyLabel(item.mode) }}
              </span>
            </div>
            <p
              v-if="item.remark"
              class="mb-0 text-xs text-n-slate-11 truncate"
              :title="item.remark"
            >
              {{
                t('SMARTICKS.DRWEB.APPOINTMENTS.REMARK', { text: item.remark })
              }}
            </p>
          </li>
        </ul>
        <Button
          v-if="hiddenAppointmentsCount > 0"
          variant="link"
          size="sm"
          class="self-start"
          :label="
            t('SMARTICKS.DRWEB.APPOINTMENTS.MORE', {
              count: hiddenAppointmentsCount,
            })
          "
          @click="moreDialog?.open()"
        />
      </div>

      <!-- 3 · open tickets (by phone — independent of the patient match) -->
      <div
        v-if="hasPhone"
        class="flex flex-col gap-2 pt-2 border-t border-n-weak"
        data-test="drweb-tickets"
      >
        <h6 class="mb-0 text-xs font-medium text-n-slate-11">
          {{ t('SMARTICKS.DRWEB.TICKETS.TITLE') }}
        </h6>
        <p
          v-if="ticketsFailed"
          class="flex items-start gap-2 mb-0 text-sm text-n-slate-11"
          dir="auto"
        >
          <Icon icon="i-lucide-plug-zap" class="shrink-0 mt-0.5 size-4" />
          <span>{{ t('SMARTICKS.DRWEB.TICKETS.FAILED') }}</span>
        </p>
        <p
          v-else-if="!openTickets.length"
          class="mb-0 text-sm text-n-slate-11"
          dir="auto"
        >
          {{ t('SMARTICKS.DRWEB.TICKETS.EMPTY') }}
        </p>
        <ul v-else class="flex flex-col gap-2 p-0 m-0 list-none">
          <li
            v-for="ticket in visibleTickets"
            :key="ticket.id"
            class="flex flex-col gap-0.5 min-h-11 sm:min-h-0"
            dir="auto"
          >
            <span
              class="text-sm text-n-slate-12 truncate"
              :title="ticket.details"
            >
              {{ ticket.details }}
            </span>
            <span
              class="flex flex-wrap items-center gap-x-2 text-xs text-n-slate-11"
            >
              <bdi class="font-inter tabular-nums">#{{ ticket.id }}</bdi>
              <span v-if="ticketStatusLabel(ticket.status)">
                {{ ticketStatusLabel(ticket.status) }}
              </span>
              <span v-if="ticket.handled_by" class="truncate">
                {{
                  t('SMARTICKS.DRWEB.TICKETS.HANDLED_BY', {
                    name: ticket.handled_by,
                  })
                }}
              </span>
              <span v-if="ticket.followup_on" class="flex items-center gap-1">
                {{ t('SMARTICKS.DRWEB.TICKETS.FOLLOWUP') }}
                <bdi class="font-inter tabular-nums">{{
                  formatDate(ticket.followup_on)
                }}</bdi>
              </span>
            </span>
          </li>
        </ul>
        <p
          v-if="hiddenTicketsCount > 0"
          class="mb-0 text-xs text-n-slate-11"
          dir="auto"
        >
          {{ t('SMARTICKS.DRWEB.TICKETS.MORE', { count: hiddenTicketsCount }) }}
        </p>
      </div>

      <!-- 4 · actions -->
      <div
        v-if="appointments.length || isMatched || drwebTenantUrl"
        class="flex flex-wrap gap-2 pt-2 border-t border-n-weak"
        data-test="drweb-actions"
      >
        <Button
          v-if="appointments.length"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-message-square-plus"
          :label="t('SMARTICKS.DRWEB.ACTIONS.REMARK')"
          @click="openRemarkDialog"
        />
        <Button
          v-if="isMatched"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-ticket"
          :label="t('SMARTICKS.DRWEB.ACTIONS.TICKET')"
          @click="openTicketDialog"
        />
        <Button
          v-if="isMatched && therapists.length"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-calendar-plus"
          :label="t('SMARTICKS.DRWEB.ACTIONS.BOOK')"
          data-test="drweb-book-open"
          @click="openBookDialog"
        />
        <p
          v-else-if="isMatched"
          class="w-full mb-0 text-xs text-n-slate-11"
          dir="auto"
        >
          {{ t('SMARTICKS.DRWEB.ACTIONS.NO_ROSTER') }}
        </p>
        <a
          v-if="drwebTenantUrl"
          :href="drwebTenantUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex"
        >
          <Button
            size="sm"
            variant="link"
            icon="i-lucide-external-link"
            :label="t('SMARTICKS.DRWEB.ACTIONS.OPEN_DRWEB')"
          />
        </a>
      </div>
    </template>

    <!-- "עוד N": every upcoming appointment, same row shape, read-only -->
    <Dialog
      ref="moreDialog"
      width="md"
      :title="t('SMARTICKS.DRWEB.APPOINTMENTS.ALL_TITLE')"
      :show-confirm-button="false"
      :cancel-button-label="t('SMARTICKS.DRWEB.CLOSE')"
      overflow-y-auto
    >
      <ul class="flex flex-col gap-3 p-0 m-0 list-none">
        <li
          v-for="item in appointments"
          :key="item.id"
          class="flex flex-col gap-0.5 text-sm"
          dir="auto"
        >
          <div class="flex flex-wrap items-center gap-x-2">
            <span class="text-xs text-n-slate-11">{{
              formatAt(item.at).weekday
            }}</span>
            <bdi class="font-inter tabular-nums text-n-slate-12">{{
              formatAt(item.at).date
            }}</bdi>
            <bdi class="font-inter tabular-nums text-n-slate-12">{{
              formatAt(item.at).time
            }}</bdi>
            <span
              class="shrink-0 text-xs px-2 py-0.5 rounded-full"
              :class="policyClass(item.mode)"
            >
              {{ policyLabel(item.mode) }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11">
            {{ item.therapist }} · {{ item.branch }}
          </span>
          <span v-if="item.remark" class="text-xs text-n-slate-11">
            {{
              t('SMARTICKS.DRWEB.APPOINTMENTS.REMARK', { text: item.remark })
            }}
          </span>
        </li>
      </ul>
    </Dialog>

    <!-- "הוסף הערה לתור": pick the appointment, then the text -->
    <Dialog
      ref="remarkDialog"
      width="md"
      :title="t('SMARTICKS.DRWEB.REMARK_DIALOG.TITLE')"
      :confirm-button-label="t('SMARTICKS.DRWEB.REMARK_DIALOG.CONFIRM')"
      :cancel-button-label="t('SMARTICKS.DRWEB.CANCEL')"
      :disable-confirm-button="!remarkText.trim() || remarkIndex === null"
      :is-loading="saving"
      @confirm="submitRemark"
    >
      <div class="flex flex-col gap-4">
        <fieldset class="flex flex-col gap-2 p-0 m-0 border-0">
          <legend class="mb-1 text-sm text-n-slate-12">
            {{ t('SMARTICKS.DRWEB.REMARK_DIALOG.PICK') }}
          </legend>
          <label
            v-for="item in appointments"
            :key="item.id"
            class="flex items-start gap-2 text-sm cursor-pointer min-h-11 sm:min-h-0"
            dir="auto"
          >
            <input
              v-model="remarkIndex"
              type="radio"
              name="drweb-remark-appointment"
              :value="item.index"
              class="mt-1 shrink-0"
            />
            <span class="flex flex-col">
              <span class="flex flex-wrap items-center gap-x-2">
                <bdi class="font-inter tabular-nums text-n-slate-12">{{
                  formatAt(item.at).date
                }}</bdi>
                <bdi class="font-inter tabular-nums text-n-slate-12">{{
                  formatAt(item.at).time
                }}</bdi>
              </span>
              <span class="text-xs text-n-slate-11">
                {{ item.therapist }} · {{ item.branch }}
              </span>
            </span>
          </label>
        </fieldset>
        <TextArea
          v-model="remarkText"
          :label="t('SMARTICKS.DRWEB.REMARK_DIALOG.TEXT')"
          :placeholder="t('SMARTICKS.DRWEB.REMARK_DIALOG.PLACEHOLDER')"
          :max-length="500"
          show-character-count
          auto-height
        />
      </div>
    </Dialog>

    <!-- "פתח פנייה": the text only; the patient and the agent name are the server's -->
    <Dialog
      ref="ticketDialog"
      width="md"
      :title="t('SMARTICKS.DRWEB.TICKET_DIALOG.TITLE')"
      :confirm-button-label="t('SMARTICKS.DRWEB.TICKET_DIALOG.CONFIRM')"
      :cancel-button-label="t('SMARTICKS.DRWEB.CANCEL')"
      :disable-confirm-button="!ticketText.trim()"
      :is-loading="saving"
      @confirm="submitTicket"
    >
      <TextArea
        v-model="ticketText"
        :label="t('SMARTICKS.DRWEB.TICKET_DIALOG.TEXT')"
        :placeholder="t('SMARTICKS.DRWEB.TICKET_DIALOG.PLACEHOLDER')"
        :max-length="1000"
        show-character-count
        auto-height
      />
    </Dialog>

    <!-- v1.1 "קבע תור": therapist → branch → date → the day's free times → the footer confirms -->
    <Dialog
      ref="bookDialog"
      width="md"
      :title="t('SMARTICKS.DRWEB.BOOK_DIALOG.TITLE')"
      :confirm-button-label="t('SMARTICKS.DRWEB.BOOK_DIALOG.CONFIRM')"
      :cancel-button-label="t('SMARTICKS.DRWEB.CANCEL')"
      :disable-confirm-button="!selectedSlot"
      :is-loading="saving"
      overflow-y-auto
      @confirm="submitBook"
    >
      <div class="flex flex-col gap-4" data-test="drweb-book">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          <span>{{ t('SMARTICKS.DRWEB.BOOK_DIALOG.THERAPIST') }}</span>
          <select
            v-model="bookTherapistId"
            class="w-full h-10 px-3 text-sm border rounded-lg outline-none bg-n-alpha-black2 border-n-weak text-n-slate-12"
            data-test="drweb-book-therapist"
          >
            <option :value="null" disabled>—</option>
            <option v-for="th in therapists" :key="th.id" :value="th.id">
              {{ th.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          <span>{{ t('SMARTICKS.DRWEB.BOOK_DIALOG.BRANCH') }}</span>
          <select
            v-model="bookBranchId"
            :disabled="!bookTherapist"
            class="w-full h-10 px-3 text-sm border rounded-lg outline-none bg-n-alpha-black2 border-n-weak text-n-slate-12 disabled:opacity-50"
            data-test="drweb-book-branch"
          >
            <option :value="null" disabled>—</option>
            <option v-for="b in bookBranches" :key="b.id" :value="b.id">
              {{ b.name }}
            </option>
          </select>
        </label>
        <p
          v-if="bookBranch && bookBranch.mode === 'info'"
          class="mb-0 text-xs text-n-slate-11"
          dir="auto"
        >
          {{ t('SMARTICKS.DRWEB.BOOK_DIALOG.INFO_CELL') }}
        </p>
        <div class="flex items-end gap-2">
          <Input
            v-model="bookDate"
            type="date"
            :label="t('SMARTICKS.DRWEB.BOOK_DIALOG.DATE')"
            :min="bookMinDate"
            :max="bookMaxDate"
            class="flex-1"
            data-test="drweb-book-date"
          />
          <bdi class="pb-3 text-xs text-n-slate-11 font-inter tabular-nums">{{
            bookDateWeekday
          }}</bdi>
        </div>
        <Button
          type="button"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-search"
          :label="t('SMARTICKS.DRWEB.BOOK_DIALOG.SHOW_SLOTS')"
          :disabled="!canSearchSlots || slotsState === 'loading'"
          :is-loading="slotsState === 'loading'"
          class="self-start"
          data-test="drweb-book-search"
          @click="loadSlots"
        />
        <div
          v-if="slotsState === 'loading'"
          class="flex flex-wrap gap-2"
          aria-busy="true"
        >
          <span
            v-for="n in 3"
            :key="n"
            class="w-16 h-11 rounded-lg motion-safe:animate-pulse bg-n-alpha-1"
          />
        </div>
        <p
          v-else-if="slotsState === 'empty'"
          class="mb-0 text-sm text-n-slate-11"
          dir="auto"
          data-test="drweb-book-empty"
        >
          {{ t('SMARTICKS.DRWEB.BOOK_DIALOG.NO_SLOTS') }}
        </p>
        <div
          v-else-if="slotsState === 'error'"
          class="flex flex-wrap items-center gap-2 text-sm text-n-slate-11"
          dir="auto"
        >
          <Icon icon="i-lucide-alert-circle" class="shrink-0 size-4" />
          <span>{{ slotsError }}</span>
          <Button
            type="button"
            size="sm"
            variant="link"
            :label="t('SMARTICKS.DRWEB.BOOK_DIALOG.RETRY')"
            @click="loadSlots"
          />
        </div>
        <template v-else-if="slotsState === 'ready'">
          <p class="mb-0 text-xs text-n-slate-11" dir="auto">
            {{ t('SMARTICKS.DRWEB.BOOK_DIALOG.PICK_SLOT') }}
          </p>
          <div class="flex flex-wrap gap-2" dir="auto" data-test="drweb-slots">
            <Button
              v-for="slot in slots"
              :key="slot.from"
              type="button"
              size="sm"
              :variant="isSelected(slot) ? 'solid' : 'faded'"
              :color="isSelected(slot) ? 'teal' : 'slate'"
              :aria-pressed="isSelected(slot)"
              class="min-h-11"
              @click="selectedSlot = slot"
            >
              <bdi class="font-inter tabular-nums">{{
                formatAt(slot.from).time
              }}</bdi>
            </Button>
          </div>
        </template>
        <a
          v-if="drwebTenantUrl && slotsState === 'ready'"
          :href="drwebTenantUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-n-slate-11 underline"
          dir="auto"
        >
          {{ t('SMARTICKS.DRWEB.BOOK_DIALOG.MORE_IN_DRWEB') }}
        </a>
        <template v-if="selectedSlot">
          <I18nT
            keypath="SMARTICKS.DRWEB.BOOK_DIALOG.SUMMARY"
            tag="p"
            class="mb-0 text-sm text-n-slate-12"
            dir="auto"
          >
            <template #therapist>{{ bookTherapist?.name }}</template>
            <template #branch>{{ bookBranch?.name }}</template>
            <template #weekday>
              {{ formatAt(selectedSlot.from).weekday }}
            </template>
            <template #date>
              <bdi class="font-inter tabular-nums">{{
                formatAt(selectedSlot.from).date
              }}</bdi>
            </template>
            <template #time>
              <bdi class="font-inter tabular-nums">{{
                formatAt(selectedSlot.from).time
              }}</bdi>
            </template>
          </I18nT>
          <p class="mb-0 text-xs text-n-slate-11" dir="auto">
            {{ t('SMARTICKS.DRWEB.BOOK_DIALOG.NO_NOTIFY') }}
          </p>
        </template>
      </div>
    </Dialog>

    <!-- v1.1 "בטל תור": the dialog is the human's challenge; the server matches the id we saw -->
    <Dialog
      ref="cancelDialog"
      type="alert"
      width="md"
      :title="t('SMARTICKS.DRWEB.CANCEL_DIALOG.TITLE')"
      :confirm-button-label="t('SMARTICKS.DRWEB.CANCEL_DIALOG.CONFIRM')"
      :cancel-button-label="t('SMARTICKS.DRWEB.CANCEL_DIALOG.BACK')"
      :disable-confirm-button="!cancelItem"
      :is-loading="saving"
      @confirm="submitCancel"
    >
      <div
        v-if="cancelItem"
        class="flex flex-col gap-3"
        data-test="drweb-cancel"
      >
        <div class="flex flex-col gap-0.5 text-sm" dir="auto">
          <span class="flex flex-wrap items-center gap-x-2">
            <span class="text-xs text-n-slate-11">{{
              formatAt(cancelItem.at).weekday
            }}</span>
            <bdi class="font-inter tabular-nums text-n-slate-12">{{
              formatAt(cancelItem.at).date
            }}</bdi>
            <bdi class="font-inter tabular-nums text-n-slate-12">{{
              formatAt(cancelItem.at).time
            }}</bdi>
          </span>
          <span class="text-xs text-n-slate-11">
            {{ cancelItem.therapist }} · {{ cancelItem.branch }}
          </span>
        </div>
        <TextArea
          v-model="cancelReason"
          :label="t('SMARTICKS.DRWEB.CANCEL_DIALOG.REASON')"
          :max-length="200"
          auto-height
        />
      </div>
    </Dialog>
  </div>
</template>
